import { CountryCode } from 'libphonenumber-js';
import { FormEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';

import { Box } from '../../../Box/Box';
import { Stack } from '../../../Stack/Stack';
import { Inline } from '../../../Inline/Inline';
import { Text } from '../../../Text/Text';
import { TextField } from '../../../TextField/TextField';

import { Flag } from '../Flag';

import { useFilteredCountries } from '../../hooks';
import { SupportedCountry } from '../../types';

import { listItemButtonStyle, listStyle } from './styles.css';

interface FlagMenuProps {
  onSelect: (countryCode: CountryCode) => void;
  onClose: () => void;
}

export function FlagMenu({ onClose, onSelect }: FlagMenuProps) {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const filteredCountries = useFilteredCountries(searchTerm);

  const handleChange = (event: FormEvent<HTMLInputElement>) => {
    setSearchTerm(event.currentTarget.value);
  };

  const handleSelect = (supportedCountry: SupportedCountry) => {
    onSelect(supportedCountry.code);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>, countryCode: CountryCode) => {
    if (event.key === 'Enter' || event.key === ' ') {
      onSelect(countryCode);
    }
  };

  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <Box
      role="menu"
      onClick={(event) => {
        event.stopPropagation();
      }}
      ref={dropdownRef}
      padding="small"
      style={{
        position: 'absolute',
        height: '363px',
        borderRadius: '8px',
        width: '400px',
        backgroundColor: 'white',
        boxShadow: '0 0 8px 0 hsla(0, 0%, 0%, 0.2)',
        overflowY: 'auto',
        overflowX: 'hidden',
        top: '4rem',
        left: '-6rem',
      }}
    >
      <Stack gap="medium">
        <TextField
          value={searchTerm}
          onChange={handleChange}
          size="small"
          adornment={
            <Box marginRight="medium">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </Box>
          }
          adornmentPosition="right"
        />
        {filteredCountries.length > 0 && (
          <ul className={listStyle}>
            {filteredCountries.map((item) => (
              <Box component="li" key={item.code}>
                <Box
                  component="div"
                  role="menuitem"
                  tabIndex={0}
                  paddingY="small"
                  paddingX="xsmall"
                  className={listItemButtonStyle}
                  key={item.code}
                  onClick={() => {
                    handleSelect(item);
                  }}
                  onKeyDown={(event) => {
                    handleKeyDown(event, item.code);
                  }}
                >
                  <Inline gap="medium">
                    <Flag countryCode={item.code} />
                    <Text>
                      {item.name} (+{item.countryCallingCode})
                    </Text>
                  </Inline>
                </Box>
              </Box>
            ))}
          </ul>
        )}
        {filteredCountries.length === 0 && (
          <Box padding="medium" display="flex" justifyContent="center">
            <Text>
              No countries found matching <b>&quot;{searchTerm}&quot;</b>
            </Text>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
